import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'

import { FeatureType } from 'clickNgo/configs/feature'

import { promotionsServiceBaseUrl } from '../configs/service'
import type { PromotionsDataset } from '../models/dataset'
import type { PromotionFocusUpdate } from '../models/focus'
import type { ProductContent, ProductContentQuery } from '../models/productContent'
import type { PromotionsSearchQuery } from '../models/query'

import { createHttpProductContentDataSource } from './httpProductContentDataSource'
import { createHttpPromotionsDataSource } from './httpPromotionsDataSource'
import type { ProductContentDataSource } from './productContentDataSource'
import type { PromotionsDataSource } from './promotionsDataSource'
import { PromotionsServiceError } from './promotionsServiceClient'

const PROMOTIONS_DATASET_TAG = 'PromotionsDataset'

/**
 * Without a configured service URL the widget keeps running on its mocks — that
 * is what lets the promotions screen be developed and tested without the service.
 *
 * The mocks are reached by a dynamic import rather than a static one. The choice
 * between them and the service is made at runtime, so a static import would put
 * the whole mock dataset — a vendored answer of the search, half a megabyte of
 * it — in the bundle of every deployment, including the ones that have a service
 * and never call this branch. Nothing can tree-shake a module a runtime ternary
 * might reach.
 */
const once = <T>(create: () => Promise<T>) => {
  let pending: Promise<T> | undefined

  // Imported on the first request that needs it and remembered after, so the
  // fetch is not paid again per request — and the data source keeps the state it
  // holds, which the focus updates of the mock dataset depend on.
  return () => (pending ??= create())
}

const httpDataSource = promotionsServiceBaseUrl ? createHttpPromotionsDataSource() : null

const httpProductContentDataSource = promotionsServiceBaseUrl
  ? createHttpProductContentDataSource()
  : null

const mockDataSource = once(async () =>
  (await import('../mocks/promotionsDataSource.mock')).createMockPromotionsDataSource()
)

const mockProductContentDataSource = once(async () =>
  (await import('../mocks/productContent.mock')).createMockProductContentDataSource()
)

const resolveDataSource = async (): Promise<PromotionsDataSource> =>
  httpDataSource ?? mockDataSource()

const resolveProductContentDataSource = async (): Promise<ProductContentDataSource> =>
  httpProductContentDataSource ?? mockProductContentDataSource()

const toQueryError = (error: unknown) => {
  if (error instanceof PromotionsServiceError) {
    return {
      error: {
        status: error.status,
        data: error.message,
        referenceId: error.referenceId,
      },
    }
  }

  return { error: { status: 'CUSTOM_ERROR' as const, data: String(error) } }
}

/** One shape for every endpoint: the data source's throw becomes the query error. */
const run = async <TData>(request: () => Promise<TData>) => {
  try {
    return { data: await request() }
  } catch (error) {
    return toQueryError(error)
  }
}

export const promotionsApi = createApi({
  reducerPath: `${FeatureType.CngApi}/promotionsApi`,
  baseQuery: fakeBaseQuery(),
  tagTypes: [PROMOTIONS_DATASET_TAG],
  endpoints: (build) => ({
    /** Carries the sidebar panel too: `filters` is part of the search result. */
    getPromotionsDataset: build.query<PromotionsDataset, PromotionsSearchQuery>({
      queryFn: (query) => run(async () => (await resolveDataSource()).load(query)),
      providesTags: [PROMOTIONS_DATASET_TAG],
    }),
    /**
     * Catalogue content of one product, read when a detail-panel tab that needs
     * it is opened. Outside the dataset tag on purpose: the catalogue lives on
     * its own schedule and knows nothing about the deal ingest, so tying its
     * invalidation to that of the promotions dataset would be tying it to an
     * unrelated event.
     */
    getProductContent: build.query<ProductContent, ProductContentQuery>({
      queryFn: (query) => run(async () => (await resolveProductContentDataSource()).load(query)),
    }),
    updatePromotionsFocus: build.mutation<void, PromotionFocusUpdate>({
      queryFn: (update) => run(async () => (await resolveDataSource()).updateFocus(update)),
      // The focus list is global per country and reorders the list, so the
      // dataset is re-read instead of being patched locally.
      invalidatesTags: [PROMOTIONS_DATASET_TAG],
    }),
  }),
})

export const {
  useGetProductContentQuery,
  useGetPromotionsDatasetQuery,
  useUpdatePromotionsFocusMutation,
} = promotionsApi
